import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { VideoCameraIcon as VideoCamera, CoinsIcon as Coins, ClockIcon as Clock, ShieldCheckIcon as ShieldCheck, ArrowRightIcon as ArrowRight, ChatCenteredDotsIcon as ChatCenteredDots, SparkleIcon as Sparkle, MagnifyingGlassIcon as MagnifyingGlass, CheckCircleIcon as CheckCircle, GlobeHemisphereWestIcon as GlobeHemisphereWest, PawPrintIcon as PawPrint, HouseIcon as House, UsersIcon as Users, StorefrontIcon as Storefront } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { NoiseOverlay } from "@/components/luxury";
import BackButton from "@/components/BackButton";
import { usePublicPrices } from "@/lib/usePublicPrices";

/**
 * Public-facing marketing landing page for the Video Tours feature.
 * Reachable at `/video-tours` for both anon visitors and signed-in users.
 *
 * Layout:
 *   1. Hero — promise + primary CTA
 *   2. 3-step "How it works" (request → seller accepts → live call)
 *   3. Stats / trust strip
 *   4. FAQ
 *   5. Final CTA
 *
 * Self-contained shell (no AppShell) so it works for unauthenticated
 * visitors during a marketing push.
 */
const STEPS = [
  {
    n: 1,
    icon: MagnifyingGlass,
    title: "Find a dog you're serious about",
    body: "Browse the marketplace, filter by breed (purebred or exotic), price, sex, or kennel. Spot a dog you'd want to fly out for? Tap the Book video tour button.",
  },
  {
    n: 2,
    icon: ChatCenteredDots,
    title: "Pick a time, the seller accepts",
    body: "Suggest a date and time that works for you (anywhere from 30 min to 30 days out). The seller has 24h to accept or counter. You only pay if they accept — if they decline or don't respond, $0.",
  },
  {
    n: 3,
    icon: VideoCamera,
    title: "Hop on a 15-minute live call",
    body: "We email both of you 30 minutes and 5 minutes before the call. Click the link, allow camera + mic in your browser, and you're live. No installs. The seller can show you the dog, the parents, the kennel, paperwork — anything you'd ask for in person.",
  },
];

const FAQ = [
  {
    q: "What's the catch with the $35 fee?",
    a: "There isn't one — the fee covers the platform's video infrastructure and the seller's time. The seller keeps 80%, the registry keeps 20%. If the seller declines or doesn't respond within 24 hours, you're not charged.",
  },
  {
    q: "Do I need to install anything?",
    a: "No. The call runs in any modern browser (Chrome, Safari, Firefox, Edge) on phone, tablet, or laptop. We send the join link by email and surface it in your /tours dashboard.",
  },
  {
    q: "What if the seller no-shows?",
    a: "Refund. If the seller fails to join the call within 5 minutes of the scheduled time, you get a full refund automatically. The platform also flags repeat offenders.",
  },
  {
    q: "Why 15 minutes?",
    a: "Long enough to actually meet the dog, walk through the kennel, and get your big questions answered. Short enough that breeders running 5+ tours a week don't burn out. If you want longer, ask the seller — many will follow up off-platform once trust is established.",
  },
  {
    q: "Is the call recorded?",
    a: "Not by us, and not by default. The seller can ask for permission to record (some buyers want a copy of the dog walking around). Both parties have to agree in-call.",
  },
];

const STAT_FALLBACKS = {
  registered_dogs: 0,
  verified_breeders: 0,
  breeds_represented: 0,
};

export default function VideoToursLanding() {
  const { user } = useAuth();
  const { prices } = usePublicPrices();
  const tourPrice = prices("video_tour", "$35");
  const tourPriceNum = prices.amount("video_tour", 35);
  const FAQ_LIVE = [
    {
      q: `What's the catch with the ${tourPrice} fee?`,
      a: "There isn't one — the fee covers the platform's video infrastructure and the seller's time. The seller keeps 80%, the registry keeps 20%. If the seller declines or doesn't respond within 24 hours, you're not charged.",
    },
    ...FAQ.slice(1),
  ];
  const [stats, setStats] = useState(STAT_FALLBACKS);
  const [sampleTour, setSampleTour] = useState(null); // { url, poster_url, caption }

  useEffect(() => {
    // Public stats endpoint — anonymous-safe — used by the press kit too.
    // We use fetch directly because /api/admin/press/stats is admin-gated;
    // the public marketplace stats fit fine here.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/stats`)
      .then((r) => r.json())
      .then((d) => setStats({
        registered_dogs: d.available || 0,
        verified_breeders: d.verified_breeders || 0,
        breeds_represented: d.distinct_breeds || 0,
      }))
      .catch(() => {});

    // Public read of the admin-managed sample-tour video URL.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/launch-pack/sample-tour`)
      .then((r) => r.json())
      .then((d) => { if (d?.url) setSampleTour(d); })
      .catch(() => {});
  }, []);

  // Absolute URL for share-card metadata. Falls back to a sane default
  // so SSR-rendered link previews still work even if env is missing.
  const SITE = (typeof window !== "undefined" && window.location?.origin)
    || "https://armstrongk9registry.com";
  const PAGE_URL = `${SITE}/video-tours`;
  const OG_IMAGE = `${SITE}/og-video-tours.png`;
  const TITLE = "Live video tours with verified breeders | Armstrong K9 Registry";
  const DESCRIPTION = "Meet the dog before you fly out. 15-minute browser-based video tours with verified purebred and exotic breeders. $35, only charged after the seller accepts. No-show? Full refund.";

  // JSON-LD Service schema gives Google a rich snippet ("Service · $35 ·
  // 15 min · Armstrong K9 Registry") and surfaces the
  // feature in voice-assistant + AI-search results.
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Live Video Tour with Dog Breeder",
    "serviceType": "Video consultation",
    "provider": {
      "@type": "Organization",
      "name": "Armstrong K9 Registry",
      "url": SITE,
      "logo": `${SITE}/logo-256.png`,
    },
    "description": DESCRIPTION,
    "areaServed": "United States",
    "offers": {
      "@type": "Offer",
      "price": tourPriceNum.toFixed(2),
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": PAGE_URL,
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "35.00",
        "priceCurrency": "USD",
        "billingDuration": "PT15M",
        "billingIncrement": 1,
      },
    },
    "termsOfService": `${SITE}/refund`,
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Armstrong K9 Registry",
    "url": SITE,
    "logo": `${SITE}/logo-256.png`,
    "sameAs": [],
    "description": "A trusted marketplace and pedigree platform for purebred and exotic dogs.",
  };

  // FAQ schema — mirrors the on-page FAQ accordion verbatim. Lets
  // Google show "People also ask" expandable rows from the search SERP.
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_LIVE.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div
      className="min-h-screen bg-[#FAFAFA] text-ink font-manrope"
      data-testid="video-tours-landing"
    >
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />

        {/* Open Graph — page-specific only. og:type, og:site_name,
            twitter:card, twitter:site live in public/index.html as
            page-agnostic defaults. */}
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter / X */}
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />

        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(orgJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      </Helmet>
      {/* Top bar */}
      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="vt-back-home">
            <img
              src="/logo-256.png"
              alt="Armstrong K9 Registry"
              className="h-8 w-8 rounded-full object-contain flex-shrink-0"
            />
            <span className="font-spectral text-lg tracking-tight group-hover:text-crimson transition-colors">
              Armstrong K9 Registry
            </span>
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-full" data-testid="vt-go-dashboard">
                  Go to dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full text-[13px]" data-testid="vt-login">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-[13px]" data-testid="vt-register">
                    Create an account
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-6">
        <BackButton fallback={user ? "/dashboard" : "/"} />
      </div>

      {/* Hero */}
      <section
        className="bg-ink text-white relative overflow-hidden"
        data-testid="vt-hero"
      >
        <NoiseOverlay opacity={0.07} />
        {/* Subtle radial glow — gold over crimson */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(201,162,39,0.28), transparent 50%), radial-gradient(circle at 15% 80%, rgba(193,39,45,0.18), transparent 55%)",
          }}
        />
        <div className="pointer-events-none absolute inset-0 bg-vignette-dark" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-4 flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> New on the registry
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
            Meet the dog before you fly out.
          </h1>
          <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-white/85 max-w-2xl">
            Live 15-minute video tours with verified breeders. Walk through the
            kennel, meet the dog and the parents, and ask every question
            you'd ask in person — without the plane ticket.
          </p>
          <div className="mt-10 flex items-center gap-3 flex-wrap">
            <Link to="/marketplace">
              <Button
                size="lg"
                className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold border-0 shadow-[0_8px_24px_rgba(201,162,39,0.35)]"
                data-testid="vt-cta-marketplace"
              >
                <Storefront size={16} weight="duotone" className="mr-1.5" />
                Browse dogs
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-white/40 text-white hover:bg-white/10 hover:text-white"
                data-testid="vt-cta-how"
              >
                How it works <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </a>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3 text-[12.5px] text-white/70">
            <span className="flex items-center gap-1.5">
              <Clock size={13} weight="duotone" className="text-gold-light" />
              15-min calls
            </span>
            <span className="flex items-center gap-1.5">
              <Coins size={13} weight="duotone" className="text-gold" />
              {tourPrice}, only after seller accepts
            </span>
            <span className="flex items-center gap-1.5">
              <VideoCamera size={13} weight="duotone" className="text-gold" />
              Browser-based, no install
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={13} weight="duotone" className="text-gold" />
              No-show full refund
            </span>
          </div>
        </div>
      </section>

      {/* Live registry strip */}
      <section className="border-y border-[#E5D08A]/50 bg-[#FBF7EC]" data-testid="vt-stats">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat
            icon={PawPrint}
            label="Available dogs"
            value={stats.registered_dogs}
          />
          <Stat
            icon={ShieldCheck}
            label="Verified breeders"
            value={stats.verified_breeders}
          />
          <Stat
            icon={GlobeHemisphereWest}
            label="Distinct breeds"
            value={stats.breeds_represented}
          />
        </div>
      </section>

      {/* Sample tour player — only renders when admin has set a URL */}
      {sampleTour?.url && (
        <section
          className="border-b border-[#E5E5E5] bg-white"
          data-testid="vt-sample-tour"
        >
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-crimson mb-3 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> See it in action
            </p>
            <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight mb-6 max-w-3xl">
              What a 15-minute video tour actually looks like.
            </h2>
            <div
              className="rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-[0_8px_24px_rgba(0,0,0,0.04)] bg-black"
              style={{ aspectRatio: "16 / 9" }}
            >
              {/youtube\.com\/embed|vimeo\.com\/video/.test(sampleTour.url) ? (
                <iframe
                  src={sampleTour.url}
                  title="Sample video tour"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="vt-sample-iframe"
                />
              ) : (
                <video
                  src={sampleTour.url}
                  poster={sampleTour.poster_url || undefined}
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="vt-sample-video"
                />
              )}
            </div>
            {sampleTour.caption && (
              <p className="text-[13px] text-[#5C5C5C] leading-relaxed mt-4 max-w-2xl">
                {sampleTour.caption}
              </p>
            )}
          </div>
        </section>
      )}

      {/* How it works */}
      <section
        id="how-it-works"
        className="max-w-5xl mx-auto px-6 md:px-10 py-20"
        data-testid="vt-how-it-works"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-crimson mb-3 flex items-center gap-1.5">
          <Sparkle size={12} weight="fill" /> How it works
        </p>
        <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight max-w-2xl">
          Three steps. Most buyers book within an hour of finding the dog.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                className="rounded-2xl border border-[#E5E5E5] bg-white p-7 hover:border-[#0E0E0E] transition-colors"
                data-testid={`vt-step-${s.n}`}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="rounded-full bg-[#0E0E0E] text-gold w-9 h-9 inline-flex items-center justify-center font-spectral text-lg font-bold">
                    {s.n}
                  </span>
                  <Icon size={18} weight="duotone" className="text-crimson" />
                </div>
                <h3 className="font-spectral text-xl tracking-tight mb-2 leading-tight">
                  {s.title}
                </h3>
                <p className="text-[13.5px] text-[#5C5C5C] leading-relaxed">
                  {s.body}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Trust ribbon */}
      <section className="bg-[#0E0E0E] text-white" data-testid="vt-trust">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">
              Why this matters
            </p>
            <h2 className="font-spectral text-3xl tracking-tight leading-tight">
              The buyer-protection layer that classified-site listings will never have.
            </h2>
            <p className="text-[14.5px] leading-relaxed text-white/80 mt-5">
              Every video tour is logged on the registry. The seller is a
              verified breeder. Payment is processed by Stripe and only released
              after both parties join. If a tour is a no-show, you get a full
              refund — automatically. This is the trust layer the dog market
              has been missing.
            </p>
          </div>
          <ul className="space-y-3">
            {[
              "Verified breeder identity on every call",
              "Stripe-backed payment, no cash, no Venmo",
              "Calls auto-end at 15 min — no overrun, no off-platform pivot",
              "Full no-show refund, no support ticket required",
              "Reminders 30 min and 5 min before — neither side forgets",
            ].map((line, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-[14px] text-white/90"
              >
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-gold flex-shrink-0 mt-0.5"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="max-w-3xl mx-auto px-6 md:px-10 py-20"
        data-testid="vt-faq"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#0F8043] mb-3 flex items-center gap-1.5">
          <Sparkle size={12} weight="fill" /> Common questions
        </p>
        <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight mb-10">
          Before you book.
        </h2>
        <div className="space-y-3">
          {FAQ_LIVE.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-[#E5E5E5] bg-white p-5 hover:border-[#0E0E0E] transition-colors"
              data-testid={`vt-faq-${i}`}
            >
              <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                <p className="font-spectral text-[17px] tracking-tight text-ink">
                  {f.q}
                </p>
                <ArrowRight
                  size={14}
                  className="text-[#6B6B6B] group-open:rotate-90 transition-transform flex-shrink-0"
                />
              </summary>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[#5C5C5C]">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="bg-[#FBF7EC] border-t border-[#E5D08A]/50"
        data-testid="vt-final-cta"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 text-center">
          <Users size={28} weight="duotone" className="text-crimson mx-auto mb-4" />
          <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight mb-4">
            Stop driving 6 hours to a parking lot.
          </h2>
          <p className="text-[14.5px] leading-relaxed text-[#5C5C5C] max-w-xl mx-auto mb-7">
            Browse the marketplace, find a breeder you'd actually buy from,
            and book a video tour. If the chemistry is there, fly out. If not,
            you saved a flight.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/marketplace">
              <Button
                size="lg"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                data-testid="vt-cta-marketplace-bottom"
              >
                <Storefront size={16} weight="duotone" className="mr-1.5" />
                Browse the marketplace
              </Button>
            </Link>
            {!user && (
              <Link to="/register">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-[#0E0E0E]"
                  data-testid="vt-cta-register-bottom"
                >
                  Create an account
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Compact footer */}
      <footer className="bg-[#0E0E0E] text-white/60 text-[12px]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-4 flex-wrap">
          <p>
            © {new Date().getFullYear()} Armstrong K9 Registry ·
            Purebreds &amp; exotic breeds
          </p>
          <div className="flex gap-4">
            <Link to="/about" className="hover:text-white" data-testid="vt-footer-about">About</Link>
            <Link to="/terms" className="hover:text-white" data-testid="vt-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-white" data-testid="vt-footer-privacy">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div
      className="rounded-xl bg-white border border-[#E5D08A]/60 p-5 flex items-center gap-4"
      data-testid={`vt-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="rounded-full bg-[#FBF7EC] border border-[#E5D08A] w-12 h-12 inline-flex items-center justify-center flex-shrink-0">
        <Icon size={18} weight="duotone" className="text-[#8A6A00]" />
      </span>
      <div>
        <p className="font-spectral text-3xl tracking-tight text-ink leading-none">
          {Number(value || 0).toLocaleString()}
        </p>
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A6A00] mt-1">
          {label}
        </p>
      </div>
    </div>
  );
}
